// BRK-S120 — the pure mapping from a bond-request extraction PROPOSAL onto the
// bond flow's OWN form state. The document path and the manual path share one
// capture surface (the BRK-S038 invariant): a proposal never spawns a parallel
// "confirm the document" form, it only produces initial values for the steps the
// broker was going to fill anyway, plus the list of keys each step must MARK for
// review.
//
// Everything the honesty rules promise is decided here, which is why it is a
// pure function with no React and no fetch (NN #1):
//   - a key the document didn't state is ABSENT, and absence NEVER writes — no
//     null, no zero, no blank overwriting something already answered;
//   - a value that fails its field's OWN client validation is DROPPED, never
//     coerced. D-10's display/wire split runs both ways: accepting "$610,000"
//     into a currency field is how the next producer learns it is allowed;
//   - a value already in the form WINS, so re-dropping a document cannot
//     clobber what the broker typed.
//
// Nothing here branches on a product, a subproduct, or a field NAME: the shape a
// value must take comes from the field's declared control type, exactly as the
// renderer draws it (D-10 rule 1).
import type { AddressValue } from "@/lib/address";
import {
  fieldError,
  isAnswered,
  type BondFieldValue,
  type BondRenderField,
  type BondValueBag,
} from "@/lib/bondFields";
import type { IntakeProposal } from "@/lib/brokerDocuments";

/** One step the prefill may write into: its key, and the fields it renders. */
export interface BondPrefillTarget {
  /** The step key the values are bagged under (`final` for the terms step). */
  stepKey: string;
  fields: readonly BondRenderField[];
}

/** A value the document stated that this configuration could not take, with the
 *  reason — surfaced to the broker, never silently swallowed (the same instinct
 *  as the intake report's unreadable-pages list). */
export interface BondPrefillDrop {
  /** The proposal's own key. */
  key: string;
  /** The field's label when the key matched a field, else null. */
  label: string | null;
  reason: string;
}

export interface BondPrefill {
  /** Values to overlay, per step key. Only keys that were actually read. */
  patch: Record<string, BondValueBag>;
  /** Composite review keys (see `reviewKey`) for every value placed. */
  reviewKeys: string[];
  dropped: BondPrefillDrop[];
  /** How many values were placed — the count the review banner names. */
  count: number;
}

/**
 * The review-marker key for one field on one step.
 *
 * Composite because `useExtractedReview` is keyed by an arbitrary string and the
 * bond flow has MANY steps: a bare field name would let two steps that happen to
 * name a field alike share one marker, and clearing one would clear the other.
 */
export function reviewKey(stepKey: string, name: string): string {
  return `${stepKey}::${name}`;
}

/**
 * Two spellings of the same field, resolved so either one lands.
 *
 * ⚠️ The note that used to sit here had it BACKWARDS, and the correction is the
 * whole reason this table earns its place. It claimed the shipped Construction
 * Bond configuration names these fields `estContractPrice` / `bidBondPenaltyPct`
 * / `contractStart` / `maintenancePct`. It does not. The live configuration was
 * read from the API on 2026-08-21, both before and after a re-seed, and each
 * time it carried the LONG spellings — `estimatedContractPrice`,
 * `bidBondPenaltyPercent`, `contractStartDate`, `maintenancePercent` — which is
 * also what the seeder that authors it declares. Acting on the wrong belief, the
 * PRODUCER was realigned to emit the short spellings, so today PMS and the
 * configuration disagree and only this table hides it.
 *
 * So this is a load-bearing adapter, not a nicety: delete it and four fields
 * stop landing. The producer should be realigned to the configuration's
 * spellings (BRK-S124), after which this remains as defence and nothing depends
 * on it.
 *
 * This is a NAME resolution and nothing else: it never changes a value, never
 * invents a field, and a key that resolves to no field in the configuration is
 * REPORTED rather than guessed at. It is deliberately not "strip Pct/Percent and
 * compare" — a rule that clever would silently pair fields nobody paired.
 */
const KEY_SYNONYMS: readonly (readonly string[])[] = [
  ["estimatedContractPrice", "estContractPrice"],
  ["bidBondPenaltyPercent", "bidBondPenaltyPct"],
  ["contractStartDate", "contractStart"],
  ["maintenancePercent", "maintenancePct"],
];

/** Every spelling of a key, itself first. */
function spellings(key: string): string[] {
  const group = KEY_SYNONYMS.find((g) => g.includes(key));
  if (!group) return [key];
  return [key, ...group.filter((k) => k !== key)];
}

/** The flat extracted record off the proposal envelope, or none at all. An
 *  absent record is extraction having produced nothing usable — a state the
 *  caller degrades from, never a crash (NN #7). */
export function bondProposalValues(
  proposal: IntakeProposal,
): Record<string, unknown> {
  // `proposed.bond.values` — beside the other targets, which is where PMS has
  // always put it. This read was `proposal.values` until BRK-S123: it found
  // `undefined` on every real response, so the flow reported "we couldn't read
  // the document" over a proposal carrying every field. Optional chaining
  // because a producer can send anything and this must degrade, not throw.
  const values = proposal.proposed?.bond?.values;
  if (!values || typeof values !== "object" || Array.isArray(values)) return {};
  return values;
}

/** A trimmed non-empty string, or null. */
function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

/** A wire address (any subset of the four members) as the composite the
 *  `AddressFieldset` is controlled by. A document that printed only "Chattanooga,
 *  TN" fills those two and leaves the rest for the broker — a PARTIAL address is
 *  honest, and `isAnswered` still refuses to call it complete. */
function asAddressValue(value: unknown): AddressValue | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  const street = str(v.street) ?? "";
  const city = str(v.city) ?? "";
  const state = (str(v.state) ?? "").toUpperCase();
  const zip = str(v.zip) ?? "";
  if (!street && !city && !state && !zip) return null;
  return { street, city, state, zip };
}

type Read =
  | { ok: true; value: BondFieldValue }
  | { ok: false; reason: string }
  /** Absence — nothing to place and nothing to complain about. */
  | { ok: false; reason: null };

const ABSENT: Read = { ok: false, reason: null };

/**
 * One wire value read as the shape its field's CONTROL holds.
 *
 * Keyed off the declared control type, never off the field's name (D-10 rule 1
 * and the licence-state trap it exists for). A type mismatch is a DROP, not a
 * conversion: the wire form is pinned, so a currency arriving as "$610,000" is a
 * producer bug, and parsing it here would hide the bug and put a number the
 * document never stated under a marker that says the document stated it.
 */
function readValue(field: BondRenderField, raw: unknown): Read {
  if (raw === null || raw === undefined) return ABSENT;

  // A closed set of states gets the shared picker whatever control the config
  // names (BRK-S111), so its value must be read as that picker's — a 2-letter
  // code, which `fieldError`'s `state` rule then judges.
  if (field.format === "state") {
    const text = str(raw);
    if (text === null) return ABSENT;
    if (typeof raw !== "string") {
      return { ok: false, reason: `${field.label} must be a state code.` };
    }
    return { ok: true, value: text.toUpperCase() };
  }

  switch (field.type) {
    case "address": {
      const address = asAddressValue(raw);
      if (address === null) {
        return typeof raw === "object"
          ? ABSENT
          : { ok: false, reason: `${field.label} must be an address.` };
      }
      return { ok: true, value: address };
    }

    case "currency":
    case "number":
    case "percent": {
      if (typeof raw !== "number" || !Number.isFinite(raw)) {
        return { ok: false, reason: `${field.label} must be a number.` };
      }
      return { ok: true, value: raw };
    }

    case "date": {
      const text = str(raw);
      if (text === null) return ABSENT;
      // The wire form is ISO `YYYY-MM-DD` and the date control is controlled by
      // exactly that string, so anything else is dropped rather than guessed —
      // "18/08/2026" and "08/18/2026" are the same characters and different days.
      if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
        return { ok: false, reason: `${field.label} must be an ISO date.` };
      }
      return { ok: true, value: text };
    }

    case "checkbox": {
      if (typeof raw !== "boolean") {
        return { ok: false, reason: `${field.label} must be yes or no.` };
      }
      return { ok: true, value: raw };
    }

    case "select": {
      const options = (field.options ?? []).map((o) =>
        typeof o === "string" ? { value: o as string | number, label: o } : o,
      );
      // An options-less select renders as free text (BRK-S098), so it takes a
      // string here for the same reason.
      if (options.length === 0) {
        const text = str(raw);
        return text === null ? ABSENT : { ok: true, value: text };
      }
      if (typeof raw !== "string" && typeof raw !== "number") {
        return {
          ok: false,
          reason: `${field.label} must be one of the options.`,
        };
      }
      // Compared as text because a stored option value may be a number (an
      // opaque vendor id), and the ORIGINAL value is what goes back — "1" is
      // rejected upstream as "must be a valid number" (BRK-S100).
      const chosen = options.find((o) => String(o.value) === String(raw));
      if (!chosen) {
        return {
          ok: false,
          reason: `${field.label} must be one of the options.`,
        };
      }
      return { ok: true, value: chosen.value };
    }

    default: {
      // text / textarea / phone / a type this build doesn't know — all of which
      // render as a text control, so all of which hold a string.
      const text = str(raw);
      if (text === null) {
        return typeof raw === "string"
          ? ABSENT
          : { ok: false, reason: `${field.label} must be text.` };
      }
      if (typeof raw !== "string") {
        return { ok: false, reason: `${field.label} must be text.` };
      }
      return { ok: true, value: text };
    }
  }
}

/**
 * A proposal → the patch to overlay on the bond flow's form state.
 *
 * `targets` is the CONFIGURATION's own step/field list, so this reads from data
 * the same way the renderer does. A proposal key that matches no field in it is
 * reported, not forced somewhere: the bond types differ in what they even ask.
 *
 * `now` is injected so the date rules stay pure and testable.
 */
export function proposalToBondPrefill(
  proposal: IntakeProposal,
  targets: readonly BondPrefillTarget[],
  now: Date = new Date(),
): BondPrefill {
  const values = bondProposalValues(proposal);

  // Every spelling of every field, so a key resolves in one lookup.
  const byName = new Map<string, { stepKey: string; field: BondRenderField }>();
  for (const target of targets) {
    for (const field of target.fields) {
      for (const spelling of spellings(field.name)) {
        if (!byName.has(spelling)) {
          byName.set(spelling, { stepKey: target.stepKey, field });
        }
      }
    }
  }

  const patch: Record<string, BondValueBag> = {};
  const reviewKeys: string[] = [];
  const dropped: BondPrefillDrop[] = [];

  for (const [key, raw] of Object.entries(values)) {
    if (raw === null || raw === undefined) continue; // absence
    const found = byName.get(key);
    if (!found) {
      // Nothing to be done with it, and nothing to invent: say so.
      if (str(raw) !== null || typeof raw !== "string") {
        dropped.push({
          key,
          label: null,
          reason: "This bond type has no field for it.",
        });
      }
      continue;
    }
    const { stepKey, field } = found;
    const read = readValue(field, raw);
    if (!read.ok) {
      if (read.reason !== null) {
        dropped.push({ key, label: field.label, reason: read.reason });
      }
      continue;
    }
    // The field's OWN rules get the last word — the same `fieldError` the step
    // gate uses, so a value this would reject can never reach the wire through
    // the prefill either (a percent over 100, a past-date rule, a bad phone).
    const invalid = fieldError(field, read.value, now);
    if (invalid !== null) {
      dropped.push({ key, label: field.label, reason: invalid });
      continue;
    }
    patch[stepKey] = { ...(patch[stepKey] ?? {}), [field.name]: read.value };
    reviewKeys.push(reviewKey(stepKey, field.name));
  }

  return { patch, reviewKeys, dropped, count: reviewKeys.length };
}

/**
 * Overlay a prefill onto the flow's form state, returning the new state and the
 * review keys that were actually WRITTEN.
 *
 * A value already answered in the form WINS and is not marked: the account-derived
 * seed the server returns at create, and anything the broker has typed, both
 * outrank a document read before either existed. Marking a value the document did
 * not put there would make the marker a lie.
 */
export function applyBondPrefill(
  base: Record<string, BondValueBag>,
  prefill: BondPrefill,
): { values: Record<string, BondValueBag>; reviewKeys: string[] } {
  const values: Record<string, BondValueBag> = { ...base };
  const written: string[] = [];
  for (const [stepKey, bag] of Object.entries(prefill.patch)) {
    const current = base[stepKey] ?? {};
    const next: BondValueBag = { ...current };
    for (const [name, value] of Object.entries(bag)) {
      // "Already answered" is the flow's own rule, so a half-typed address and
      // an empty string both still take the document's value.
      if (isAnsweredHere(current[name])) continue;
      next[name] = value;
      written.push(reviewKey(stepKey, name));
    }
    values[stepKey] = next;
  }
  return { values, reviewKeys: written };
}

/**
 * The form state with an EARLIER prefill's still-unreviewed values removed.
 *
 * For the one case that needs it: the broker drops a document and then changes
 * the bond type. The same read has to be re-mapped, because the field set is a
 * different one — and a value that belonged to the old field set is not the
 * broker's answer to the new one. Left in place it would be invisible (no field
 * renders it) and still ride along on that step's save.
 *
 * Only UNREVIEWED values go: one the broker has confirmed or edited is theirs,
 * whatever the document originally said.
 */
export function withoutBondPrefill(
  values: Record<string, BondValueBag>,
  prefill: BondPrefill,
  stillPending: (key: string) => boolean,
): Record<string, BondValueBag> {
  const out: Record<string, BondValueBag> = { ...values };
  for (const [stepKey, bag] of Object.entries(prefill.patch)) {
    const current = values[stepKey];
    if (!current) continue;
    const next: BondValueBag = { ...current };
    for (const name of Object.keys(bag)) {
      if (stillPending(reviewKey(stepKey, name))) delete next[name];
    }
    out[stepKey] = next;
  }
  return out;
}

/** Whether a bag already holds an answer at this key. Type-only — the field
 *  definition isn't needed to know that `undefined` and `""` are not answers,
 *  and an address is judged by `isAnswered`'s own completeness rule. */
function isAnsweredHere(value: BondFieldValue | undefined): boolean {
  if (value === undefined) return false;
  if (typeof value === "object") {
    return isAnswered({ name: "", label: "", type: "address" }, value);
  }
  return isAnswered({ name: "", label: "", type: "text" }, value);
}
